import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Formik, Field } from 'formik';
import { Form } from 'react-bootstrap';
import { BlobServiceClient } from '@azure/storage-blob';
import ImageUploadWithPreview from './imageUploadWithPreview';
import { toast } from 'react-toastify';
import SelectBox from '../../shared/controls/form/selectBox';
import PasswordBox from '../../shared/controls/form/passwordBox';

const validationSchema = Yup.object().shape({
  empId: Yup.string()
    .required('Employee ID is required')
    .min(1, 'Employee ID must be at least 1 character')
    .max(10, 'Employee ID must be less than 10 characters long'),

  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),

  userName: Yup.string()
    .required('User Name is required')
    .test(
      'is-valid-email',
      'Invalid UserName format',
      (value) =>
        !value ||
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)
    ),

  phoneNumber: Yup.string().required('Phone Number is required'),

  email: Yup.string()
    .email('Invalid email format')
    .required('Email is required'),

  auth_type: Yup.string().when('type', {
    is: (type) => type === 'add',
    then: (schema) => schema.required('Auth Type is required'),
    otherwise: (schema) => schema.notRequired(),
  }),

  oid: Yup.string().when(['auth_type'], {
    is: (auth_type) => auth_type === 'azure',
    then: (schema) => schema.required('Azure Object Id is required'),
    otherwise: (schema) => schema.notRequired(),
  }),

  password: Yup.string().when(['auth_type', 'type'], {
    is: (auth_type, type) => auth_type === 'db' && type === 'add',
    then: (schema) => schema.required('Password is required'),
    otherwise: (schema) => schema.notRequired(),
  }),

  confirmPassword: Yup.string().when(['auth_type', 'type'], {
    is: (auth_type, type) => auth_type === 'db' && type === 'add',
    then: (schema) =>
      schema
        .required('Confirm Password is required')
        .oneOf([Yup.ref('password')], 'Passwords must match'),
    otherwise: (schema) => schema.notRequired(),
  }),
});



const AddUserForm = ({ handleCloseAddForm, open, handleFormSubmit, userData, userProfileFlag }) => {
  const [formData, setFormData] = useState(userData);
  const [errormsg, setErrorMsg] = useState({});
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedRoleIDs, setSelectedRoleIDs] = useState(
    userData ? userData.roles.map((role) => role.id) : []
  );
  const [formEdited, setFormEdited] = useState(false);

  // const [selectedOptions, setSelectedOptions] = useState({});
  const [imagefile, setImageFile] = useState(null);
  const [imageUploadStatus, setImageUploadStatus] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const auth_typeOptions = [
    { id: 'azure', label: 'Azure' },
    { id: 'db', label: 'DB' },
  ];

  useEffect(() => {
    if (formData?.auth_type) {
      const selected = auth_typeOptions.find((option) => option.id === formData.auth_type);

      setSelectedOptions((prev) => ({
        ...prev,
        auth_type: selected,
      }));
    }
  }, [formData]);

  const handleChange = (selectedOption, name) => {
    setSelectedOptions((prevState) => ({
      ...prevState,
      [name]: selectedOption,
    }));

    handleLookupDropDownChange(selectedOption.id, selectedOption, name);
  };

  const handleLookupDropDownChange = (selectedId, selectedOption, name) => {
    const selectedValue = selectedOption.value;

    if (name === 'auth_type') {
      setFormData((prev) => ({
        ...prev,
        auth_type: selectedValue, // store actual value (azure/db)

        // Optional: clear opposite fields automatically
        ...(selectedValue === 'azure' && {
          password: '',
          confirmPassword: '',
        }),
        ...(selectedValue === 'db' && {
          oid: '',
        }),
      }));
    }
  };

  const handleChangeForm = (e) => {
    const { name, value } = e.target;
    setErrorMsg((prevErrorMsg) => ({
      ...prevErrorMsg,
      [name]: undefined,
    }));
    let convertedValue = value;
    if (name === 'workHours') {
      convertedValue = value.replace(/[^0-9:.]|(?<=[:.]\d{2})\d/g, '');
    } else if (name === 'startDate' || name === 'endDate') {
      // Convert date values to ISO format
      convertedValue = new Date(value).toISOString();
    } else if (name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setErrorMsg((prevErrorMsg) => ({
          ...prevErrorMsg,
          [name]: 'Invalid email format',
        }));
      }
      // This ensures that valid email addresses are not filtered out
      convertedValue = value;
    } else if (name === 'phoneNumber') {
      const input = value.replace(/\D/g, ''); // Remove non-numeric characters
      if (!input && value.trim() === '') {
        // Show required validation when the field is empty
        setErrorMsg((prevErrorMsg) => ({
          ...prevErrorMsg,
          [name]: 'Phone number is required',
        }));
      } else if (input.length < 10) {
        // Show validation for at least 10 digits
        setErrorMsg((prevErrorMsg) => ({
          ...prevErrorMsg,
          [name]: 'Phone number must be at least 10 digits',
        }));
      } else {
        // Remove error message when the input is valid
        setErrorMsg((prevErrorMsg) => ({
          ...prevErrorMsg,
          [name]: undefined,
        }));
        // Format the phone number
        convertedValue = formatPhoneNumber(input);
      }
    }

    if (name === 'dos') {
      const totalExperience = calculateVintage(value);
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
        totalExperience: totalExperience,
      }));
    } else if (name === 'doj') {
      // Assuming 'doj' is the Date of Joining field
      const kmgVintage = calculateVintage(value); // Calculate kmgVintage based on doj
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
        kmgVintage: kmgVintage, // Update kmgVintage in formData
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: convertedValue,
      }));
    }

    setFormEdited(true);
  };

  const formatPhoneNumber = (input) => {
    // Format the phone number as (###) ### - ####
    const areaCode = input.slice(0, 3);
    const prefix = input.slice(3, 6);
    const lineNumber = input.slice(6, 10);

    let formattedNumber = '';

    if (areaCode) {
      formattedNumber += `(${areaCode}`;
    }
    if (prefix) {
      formattedNumber += `) ${prefix}`;
    }
    if (lineNumber) {
      formattedNumber += ` - ${lineNumber}`;
    }

    return formattedNumber;
  };

  const handlePhoneNumberKeyPress = (e) => {
    const pattern = /[0-9]/; // Accept only numeric characters
    const inputChar = String.fromCharCode(e.charCode);
    if (!pattern.test(inputChar)) {
      e.preventDefault(); // Prevent the character from being entered
    }
  };

  const handleSubmit = async (formData) => {
    if (imagefile && formData?.userName) {
      const newFile = new File([imagefile], formData?.userName, {
        type: imagefile.type,
      });
      saveUploadImage(newFile);
    }

    // Remove non-numeric characters from the phone number
    const phoneNumberWithoutMask = formData?.phoneNumber?.replace(/\D/g, '');

    // Update the form data with the phone number without masking
    const updatedFormData = {
      ...formData,
      phoneNumber: phoneNumberWithoutMask,
    };
    if (updatedFormData.auth_type === 'azure') {
      delete updatedFormData.password;
      delete updatedFormData.confirmPassword;
    }

    if (updatedFormData.auth_type === 'db') {
      delete updatedFormData.oid;
    }

    // Validate formData using validationSchema
    try {
      await validationSchema.validate(updatedFormData, { abortEarly: false });

      // Update updatedFormData with formatted values
      const {
        extension,
        family_name,
        preferred_username,
        roles,
        secondaryEmail,
        given_name,
        confirmPassword,
        ...cleanedFormData
      } = updatedFormData;

      // Determine whether to submit based on edit type
      if (userData && userData.type === 'edit') {
        handleFormSubmit(cleanedFormData);
      } else {
        handleFormSubmit(cleanedFormData);
      }
    } catch (error) {
      const fieldErrors = {};
      error?.inner?.forEach((err) => {
        fieldErrors[err.path] = err.message;
      });
      setErrorMsg(fieldErrors);
      console.error('Validation Error:', fieldErrors);
    }
  };

  const calculateVintage = (doj) => {
    if (!doj) return '';
    const dojDate = new Date(doj);
    const currentDate = new Date();

    let years = currentDate.getFullYear() - dojDate.getFullYear();
    let months = currentDate.getMonth() - dojDate.getMonth();

    if (months < 0) {
      years--;
      months += 12;
    }

    const totalYears = years + months / 12;

    //formData.totalExperience=totalYears
    return totalYears.toFixed(1); // Keeping one decimal place
  };

  const currentDate = new Date().toISOString().split('T')[0];

  const [isExpand, setIsExpand] = useState(false);

  const handleExpand = () => {
    setIsExpand((prev) => !prev);
  };

  const handleUploadImage = async (imagefile) => {
    setImageFile(imagefile);
    setFormEdited(true);
  };
  const saveUploadImage = async (imagefile) => {
    const sasUrl = process.env.REACT_APP_AZURE_STORAGE_SAS_URL;
    const blobServiceClient = new BlobServiceClient(sasUrl);
    const containerName = process.env.REACT_APP_AZURE_STORAGE_CONTAINER_NAME; // container name
    const containerClient = blobServiceClient.getContainerClient(containerName);

    try {
      const blockBlobClient = containerClient.getBlockBlobClient(imagefile.name);
      await blockBlobClient.uploadBrowserData(imagefile); // Use `uploadBrowserData` instead of `uploadBrowserFile`
      toast.success('Profile Image Uploaded successfully!', {
        position: 'top-right',
      });
      setImageUploadStatus(true);
    } catch (error) {
      alert('File upload failed. See console for details.');
      setImageUploadStatus(false);
    }
  };
  return (
    <>
      <div className={isExpand ? 'modal-dialog maxContent' : 'modal-dialog defaultContent'}>
        <div className="modal-content" style={{height: "auto"}}>
          <div className="modal-header" style={{ padding: '1rem 1.6rem' }}>
            {!userProfileFlag ? (
              <h5 className="modal-title" id="modalLabel">
                {formData?.type === 'add' ? 'Add User' : 'Update User'}
              </h5>
            ) : (
              <h5 className="modal-title" id="modalLabel">
                User Profile Details
              </h5>
            )}

            <div className="modalHdrRight">
              <button onClick={handleExpand} className={isExpand ? 'maxBtn me-4' : 'minBtn me-4'}>
                <div
                  className={isExpand ? 'maxBtnsvg' : ''}
                  style={{ display: isExpand ? 'block' : 'none' }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16.558"
                    height="16.558"
                    viewBox="0 0 16.558 16.558"
                  >
                    <path
                      id="minimize"
                      d="M3.763,11.391H8.339m0,0v4.577m0-4.577L3,16.73M15.967,8.339H11.391m0,0V3.763m0,4.577L16.73,3"
                      transform="translate(-1.586 -1.586)"
                      fill="none"
                      stroke="#333"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <div
                  className={!isExpand ? 'minBtnsvg' : ''}
                  style={{
                    display: !isExpand ? 'block' : 'none',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16.561"
                    height="16.561"
                    viewBox="0 0 16.561 16.561"
                  >
                    <path
                      id="maximize"
                      d="M11.392,8.341,16.733,3m0,0H12.155m4.578,0V7.578M8.341,11.392,3,16.733m0,0H7.578M3,16.733V12.155"
                      transform="translate(-1.586 -1.586)"
                      fill="none"
                      stroke="#333"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
              </button>

              <button
                type="button"
                className="custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={handleCloseAddForm}
              ></button>
            </div>
          </div>
          <div className="modal-body">
            <Formik
              initialValues={formData}
              onSubmit={(values) => handleSubmit(values)}
              validationSchema={validationSchema}
            >
              <Form>
                <div className="row">
                  <div className="col-12 col-sm-12 col-md-12">
                    <ImageUploadWithPreview
                      uploadFile={handleUploadImage}
                      imageUploadStatus={imageUploadStatus}
                      formData={formData}
                      userProfileFlag={userProfileFlag}
                    />
                  </div>
                  <div className="col-12 col-sm-12 col-md-12 mt-1">
                    <div className="row">
                      <div className="col-12 col-sm-12 col-md-4">
                        <div className="input-container">
                          <label htmlFor="firstName">
                            Employee ID
                            <span className="required-star">*</span>
                          </label>
                          <Field
                            className="formik-input"
                            type="text"
                            name="empId"
                            fullwidth
                            autoComplete="given-name"
                            variant="outlined"
                            value={formData?.empId}
                            onChange={handleChangeForm}
                            disabled={
                              userProfileFlag
                              // checkCanEditField("projectId")
                            }
                          />
                          {errormsg.empId && <div className="error-message">{errormsg.empId}</div>}
                        </div>
                      </div>
                      <div className="col-12 col-sm-12 col-md-4">
                        <div className="input-container">
                          <label htmlFor="firstName">
                            First Name<span className="required-star">*</span>
                          </label>
                          <Field
                            id="firstName"
                            className="formik-input"
                            type="text"
                            name="firstName"
                            fullwidth
                            autoComplete="given-name"
                            variant="outlined"
                            value={formData?.firstName}
                            onChange={handleChangeForm}
                            disabled={
                              formData?.type === 'edit' || userProfileFlag
                              // checkCanEditField("projectId")
                            }
                          />
                          {errormsg.firstName && (
                            <div className="error-message">{errormsg.firstName}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-12 col-sm-12 col-md-4">
                        <div className="input-container">
                          <label htmlFor="firstName">
                            Last Name<span className="required-star">*</span>
                          </label>
                          <Field
                            className="formik-input"
                            type="text"
                            name="lastName"
                            fullwidth
                            autoComplete="given-name"
                            variant="outlined"
                            value={formData?.lastName}
                            onChange={handleChangeForm}
                            disabled={
                              formData?.type === 'edit' || userProfileFlag
                              // checkCanEditField("projectId")
                            }
                          />
                          {errormsg.lastName && (
                            <div className="error-message">{errormsg.lastName}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-12 col-sm-12 col-md-4">
                        <div className="input-container">
                          <label htmlFor="firstName">
                            User Name (Email)
                            <span className="required-star">*</span>
                          </label>
                          <Field
                            className="formik-input"
                            required
                            type="text"
                            name="userName"
                            id="name"
                            variant="outlined"
                            value={formData?.userName}
                            onChange={handleChangeForm}
                            placeholder="example@gmail.com"
                            disabled={
                              userProfileFlag
                              // checkCanEditField("projectId")
                            }
                          />
                          {errormsg.userName && (
                            <div className="error-message">{errormsg.userName}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-12 col-sm-12 col-md-4">
                        <div className="input-container">
                          <label htmlFor="firstName">
                            Phone Number
                            <span className="required-star">*</span>
                          </label>
                          <Field
                            className="formik-input"
                            type="text"
                            name="phoneNumber"
                            fullwidth
                            autoComplete="given-name"
                            variant="outlined"
                            value={formData?.phoneNumber}
                            onChange={handleChangeForm}
                            onKeyPress={handlePhoneNumberKeyPress}
                            placeholder="(###) ### - ####"
                            disabled={
                              userProfileFlag
                              // checkCanEditField("projectId")
                            }
                          />

                          {errormsg.phoneNumber && (
                            <div className="error-message">{errormsg.phoneNumber}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-12 col-sm-12 col-md-4">
                        <div className="input-container">
                          <label htmlFor="firstName">
                            Email<span className="required-star">*</span>
                          </label>
                          <Field
                            className="formik-input"
                            type="text"
                            name="email"
                            fullwidth
                            autoComplete="given-name"
                            variant="outlined"
                            value={formData?.email}
                            onChange={handleChangeForm}
                            placeholder="example@gmail.com"
                            disabled={
                              userProfileFlag
                              // checkCanEditField("projectId")
                            }
                          />
                          {errormsg.email && <div className="error-message">{errormsg.email}</div>}
                        </div>
                      </div>
                      <div className="col-12 col-sm-12 col-md-4">
                        <div className="input-container">
                          <SelectBox
                            label="Auth Type"
                            name="auth_type"
                            placeholder="Select Auth Type"
                            selectedOptions={selectedOptions?.auth_type}
                            handleSelect={handleChange}
                            options={auth_typeOptions}
                            optionKeys={['id', 'label']}
                            errormsg={errormsg?.auth_type}
                            disabled={formData?.type === 'edit'} // ✅ disable in update
                          />
                        </div>
                      </div>

                      {formData?.auth_type === 'azure' && (
                        <div className="col-12 col-sm-12 col-md-4">
                          <div className="input-container">
                            <label>
                              Azure Object Id<span className="required-star">*</span>
                            </label>
                            <Field
                              className="formik-input"
                              type="text"
                              name="oid"
                              value={formData?.oid}
                              onChange={handleChangeForm}
                              disabled={formData?.type === 'edit'}
                            />
                            {errormsg.oid && <div className="error-message">{errormsg.oid}</div>}
                          </div>
                        </div>
                      )}
                      {formData?.auth_type === 'db' && formData?.type === "add" &&(
                        <>
                          <div className="col-12 col-sm-12 col-md-4">
                            <div className="input-container">
                              <PasswordBox
                              label="Password"
                              name="password"
                              placeholder="Enter Password"
                              value={formData?.password}
                              handleChange={handleChangeForm}
                              errormsg={errormsg?.password}
                              isRequired
                              variant="text"
                            />
                              
                            </div>
                            
                          </div>

                          <div className="col-12 col-sm-12 col-md-4">
                            <div className="input-container">
                              <PasswordBox
                              label="Confirm Password"
                              name="confirmPassword"
                              placeholder="Confirm Password"
                              value={formData?.confirmPassword}
                              handleChange={handleChangeForm}
                              errormsg={errormsg?.confirmPassword}
                              isRequired
                              variant="text"
                            />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Form>
            </Formik>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-primary"
              data-bs-dismiss="modal"
              onClick={handleCloseAddForm}
            >
              Cancel
            </button>
            {/* {!userProfileFlag && ( */}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                handleSubmit(formData);
              }}
              disabled={
                formData?.type === 'edit' && !formEdited
                // &&
                // userProfileFlag
              }
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddUserForm;
